import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/_services/auth.service';
import { Constants } from 'app/app.constants';
import { environment } from 'environments/environment';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { MessageSearchService } from './message-search.service';

/**
 * Shared logic for redirecting Business Analysts with pending messages to Message Search.
 * Used after login (when not using Duo) and when landing on dashboard (e.g. after Duo return).
 */
@Injectable({
    providedIn: 'root'
})
export class BAMessageRedirectService {

    private getEntitiesByUserUrl = environment.ipserver + 'AnA-backend/private/entity/getEntitiesByUser';

    constructor(
        private http: HttpClient,
        private messageSearchService: MessageSearchService,
        private router: Router,
        private auth: AuthService
    ) {}

    /**
     * If the current user is a Business Analyst with pending messages, navigates to Message Search;
     * otherwise navigates to returnUrl. Uses AuthService.getUser() or localStorage 'user' for username.
     * @param returnUrl URL to navigate to when user is not BA or has no pending messages
     * @returns Observable that completes when the redirect decision has been applied
     */
    redirectToMessageSearchIfNeeded(returnUrl: string): Observable<void> {
        const targetUrl = returnUrl || '/';
        const userFromAuth = this.auth.getUser();
        const loggedInUser = (userFromAuth?.username || (typeof localStorage !== 'undefined' && localStorage.getItem('user')) || '').trim();
        if (!loggedInUser) {
            this.router.navigateByUrl(targetUrl);
            return of(undefined);
        }
        const applicationContext = (environment.host || '').replace(/\/$/, '') + '/dashboard';
        return this.http.get<string[]>(this.getEntitiesByUserUrl, {
            params: {
                entityType: environment.businessAnalystEntityKey,
                application: applicationContext
            }
        }).pipe(
            take(1),
            switchMap(data => {
                if (!data || data.length === 0) {
                    if (this.router.url !== targetUrl) {
                        this.router.navigateByUrl(targetUrl);
                    }
                    return of(undefined);
                }
                const end = new Date();
                const start = new Date();
                start.setDate(start.getDate() - Constants.MAX_DATE_RANGE);
                const pad = (n: number) => String(n).padStart(2, '0');
                const startDate = `${start.getFullYear()}/${pad(start.getMonth() + 1)}/${pad(start.getDate())}`;
                const endDate = `${end.getFullYear()}/${pad(end.getMonth() + 1)}/${pad(end.getDate())}`;
                const messagesSearchUrl = environment.ipserver + 'o2c-backend/private/messages/search';
                const applicationForO2C = (environment.host || '') + 'o2c';
                const paramsForReq = new HttpParams()
                    .set('receivedDateFrom', startDate)
                    .set('receivedDateTo', endDate)
                    .set('customerNumber', '')
                    .set('businessAnalyst', loggedInUser)
                    .set('invoiceNumber', '')
                    .set('poNumber', '')
                    .set('status', '')
                    .set('user', loggedInUser)
                    .set('component', '/inquiries/messageSearch')
                    .set('application', applicationForO2C);

                return this.http.get<any[]>(messagesSearchUrl, { params: paramsForReq }).pipe(
                    take(1),
                    map(msgs => {
                        if (msgs && msgs.length > 0) {
                            this.router.navigate(['/o2c/inquiries/messageSearch'], {
                                queryParams: {
                                    businessAnalyst: loggedInUser,
                                    receivedDateFrom: startDate,
                                    receivedDateTo: endDate,
                                    status: ''
                                }
                            });
                        } else if (this.router.url !== targetUrl) {
                            this.router.navigateByUrl(targetUrl);
                        }
                        return undefined;
                    })
                );
            }),
            take(1)
        );
    }
}
